import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import type { HighlightDto } from "@/types";

/**
 * Catalogue of icon presets the editor can pick in admin.
 * `key` is what we store in highlight_messages.icon;
 * the rendering chooses an SVG + colour.
 */
export const HIGHLIGHT_ICONS: Array<{
  key: string; label: string; bg: string; fg: string; render: () => JSX.Element;
}> = [
  { key: "critical", label: "Kritisch",            bg: "var(--color-error)",   fg: "#fff",
    render: () => <span className="font-extrabold leading-none">!</span> },
  { key: "warning",  label: "Achtung",      bg: "#E0A526", fg: "#222",
    render: () => <span className="font-extrabold leading-none">!</span> },
  { key: "blocker",  label: "Blocker",             bg: "#8C1F1F", fg: "#fff",
    render: () => <span className="font-extrabold leading-none">X</span> },
  { key: "info",     label: "Information",        bg: "#3B6AAB", fg: "#fff",
    render: () => <span className="font-extrabold leading-none italic">i</span> },
  { key: "calendar", label: "Termin",     bg: "var(--color-primary)", fg: "#fff",
    render: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/>
      </svg>
    ) },
];

function iconFor(key: string | undefined) {
  return HIGHLIGHT_ICONS.find((i) => i.key === key) ?? HIGHLIGHT_ICONS[0];
}

/**
 * "Hervorgehobene Mitteilung" bar (spec 2.3).
 *
 * - Centered in the page (max width matches the content container).
 * - Each row leads with a coloured badge whose shape depends on the
 *   editor's icon choice (critical/warning/blocker/info/calendar).
 * - Bar collapses to nothing when the API returns no active rows.
 */
export function HighlightBar() {
  const { data: highlights = [] } = useQuery<HighlightDto[]>({
    queryKey: ["public-highlights"],
    queryFn: () => apiGet<HighlightDto[]>("/public/highlights"),
    staleTime: 60_000,
  });
  if (highlights.length === 0) return null;

  return (
    <section aria-label="Hervorgehobene Mitteilung" className="border-b border-border bg-bg-soft">
      <div className="max-w-content mx-auto px-4 lg:px-6 py-3 flex flex-col items-center gap-2">
        {highlights.map((h) => <HighlightRow key={h.id} h={h} />)}
      </div>
    </section>
  );
}

function HighlightRow({ h }: { h: HighlightDto }) {
  const ic = iconFor(h.icon);

  const inner = (
    <span className="flex items-center justify-center gap-3 max-w-3xl mx-auto">
      <span
        aria-hidden
        className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
        style={{ background: h.color || ic.bg, color: ic.fg }}
        title={ic.label}
      >
        {ic.render()}
      </span>
      <span className="min-w-0 text-center">
        <span className="font-semibold text-text text-base">{h.title}</span>
        {h.bodyHtml && (
          <span
            className="ml-2 text-sm text-text-muted"
            // body_html is OWASP-sanitised on the server before storage.
            dangerouslySetInnerHTML={{ __html: stripBlockTags(h.bodyHtml) }}
          />
        )}
      </span>
    </span>
  );

  if (!h.linkUrl) return <div className="text-base w-full text-center">{inner}</div>;
  const external = /^https?:\/\//i.test(h.linkUrl);
  return external ? (
    <a href={h.linkUrl} target="_blank" rel="noopener noreferrer"
       className="w-full text-center text-base hover:text-primary transition-colors">{inner}</a>
  ) : (
    <Link to={h.linkUrl} className="w-full text-center text-base hover:text-primary transition-colors">{inner}</Link>
  );
}

function stripBlockTags(html: string): string {
  return html
    .replace(/<\/?(p|div|section|article|header|footer|h[1-6])(\s[^>]*)?>/gi, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}
